import { Fragment, RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductType } from "../../shared/enums/ProductType";
import { productCardHelper } from "../../shared/helpers/productCardHelper";
import { ListObject } from "../../shared/types/ListObject";
import { ProductItem } from "../../shared/types/ProductItem";
import { useProductByType } from "../../store/hooks/useProductByType";
import { ProductData } from "../../store/interfaces/productData";
import ProductCard, { ProductCardMode } from "../ProductCard/ProductCard";
import { useFilterProductItems } from "./hooks/useFilterProductItems";
import { useProductTypes } from "./hooks/useProductTypes";
import { useSelectedLowerBracketAndTiltrotator } from "./hooks/useSelectedLowerBracketAndTiltrotator";
import "./Tools.scss";
import ToolsProductItemsList from "./ToolsProductItemsList";

interface ToolsProps {
  onListObjectChange(productItem: ListObject): void;
  onProductTypeChange(productType: ProductType): void;
  refToolsItems: RefObject<HTMLDivElement>;
  refToolsProductItemsList: RefObject<HTMLDivElement>;
  selectedToolsProductItems: ProductItem[];
  selectedToolsProductTypes: ProductType[];
}

const Tools = ({
  onListObjectChange,
  onProductTypeChange,
  refToolsItems,
  refToolsProductItemsList,
  selectedToolsProductItems,
  selectedToolsProductTypes,
}: ToolsProps) => {
  const [productItems, setProductItems] = useState([]);
  const { toolsProductTypes } = useProductTypes();
  const { t } = useTranslation("tools");

  const { selectedLowerBracket, selectedTiltrotator } =
    useSelectedLowerBracketAndTiltrotator();
  const asphaltCuttersData = useProductByType(
    ProductType.AsphaltCutters
  ) as ProductData;
  const bucketsData = useProductByType(ProductType.Buckets) as ProductData;
  const compactorsData = useProductByType(
    ProductType.Compactors
  ) as ProductData;
  const gradingBeamsData = useProductByType(
    ProductType.GradingBeams
  ) as ProductData;
  const grapplesData = useProductByType(ProductType.Grapples) as ProductData;
  const grappleModulesData = useProductByType(
    ProductType.GrappleModules
  ) as ProductData;
  const railGrapplesData = useProductByType(
    ProductType.RailGrapples
  ) as ProductData;
  const rippersData = useProductByType(ProductType.Rippers) as ProductData;
  const { filteredProductItems: filteredAsphaltCutters } =
    useFilterProductItems({
      productItemsData: asphaltCuttersData,
      productType: ProductType.AsphaltCutters,
      selectedLowerBracket,
      selectedTiltrotator,
    });
  const { filteredProductItems: filteredCompactors } = useFilterProductItems({
    productItemsData: compactorsData,
    productType: ProductType.Compactors,
    selectedLowerBracket,
    selectedTiltrotator,
  });
  const { filteredProductItems: filteredGradingBeams } = useFilterProductItems({
    productItemsData: gradingBeamsData,
    productType: ProductType.GradingBeams,
    selectedLowerBracket,
    selectedTiltrotator,
  });
  const { filteredProductItems: filteredGrapples } = useFilterProductItems({
    productItemsData: grapplesData,
    productType: ProductType.Grapples,
    selectedLowerBracket,
    selectedTiltrotator,
  });
  const { filteredProductItems: filteredGrappleModules } =
    useFilterProductItems({
      productItemsData: grappleModulesData,
      productType: ProductType.GrappleModules,
      selectedLowerBracket,
      selectedTiltrotator,
    });
  const { filteredProductItems: filteredRailGrapples } = useFilterProductItems({
    productItemsData: railGrapplesData,
    productType: ProductType.RailGrapples,
    selectedLowerBracket,
    selectedTiltrotator,
  });
  const { filteredProductItems: filteredRippers } = useFilterProductItems({
    productItemsData: rippersData,
    productType: ProductType.Rippers,
    selectedLowerBracket,
    selectedTiltrotator,
  });
  const { filteredProductItems: filteredBuckets } = useFilterProductItems({
    productItemsData: bucketsData,
    productType: ProductType.Buckets,
    selectedLowerBracket,
    selectedTiltrotator,
  });

  useEffect(() => {
    if (!selectedToolsProductTypes || selectedToolsProductTypes.length === 0) {
      setProductItems([]);
    }
    const tempProductItems = [];

    selectedToolsProductTypes.forEach((selectedProductType) => {
      switch (selectedProductType) {
        case ProductType.AsphaltCutters:
          tempProductItems.push(...filteredAsphaltCutters);
          break;

        case ProductType.Buckets:
          tempProductItems.push(...filteredBuckets);
          break;

        case ProductType.Compactors:
          tempProductItems.push(...filteredCompactors);
          break;

        case ProductType.GradingBeams:
          tempProductItems.push(...filteredGradingBeams);
          break;

        case ProductType.Grapples:
          tempProductItems.push(...filteredGrapples);
          break;

        case ProductType.GrappleModules:
          tempProductItems.push(...filteredGrappleModules);
          break;

        case ProductType.RailGrapples:
          tempProductItems.push(...filteredRailGrapples);
          break;

        case ProductType.Rippers:
          tempProductItems.push(...filteredRippers);
          break;

        default:
          return 0;
      }
    });

    setProductItems(tempProductItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToolsProductTypes, selectedLowerBracket, selectedTiltrotator]);

  const isSelected = (productType: ProductType): boolean => {
    if (!selectedToolsProductTypes || selectedToolsProductTypes.length === 0) {
      return false;
    }
    return selectedToolsProductTypes.includes(productType);
  };

  const getNumberOfTools = (productType: ProductType): number => {
    switch (productType) {
      case ProductType.AsphaltCutters:
        return filteredAsphaltCutters.length;

      case ProductType.Compactors:
        return filteredCompactors.length;

      case ProductType.GradingBeams:
        return filteredGradingBeams.length;

      case ProductType.Grapples:
        return filteredGrapples.length;

      case ProductType.GrappleModules:
        return filteredGrappleModules.length;

      case ProductType.RailGrapples:
        return filteredRailGrapples.length;

      case ProductType.Rippers:
        return filteredRippers.length;

      case ProductType.Buckets:
        return filteredBuckets.length;

      default:
        return 0;
    }
  };

  const getPreContent = (numberOfTools: number): string => {
    if (numberOfTools === 1) {
      return `${numberOfTools} ${t("choise")}`;
    }
    return `${numberOfTools} ${t("choises")}`;
  };

  const sortedToolsProductTypes = toolsProductTypes.sort((a, b) =>
    a.name > b.name ? 1 : b.name > a.name ? -1 : 0
  );

  return (
    <>
      <div ref={refToolsItems} className="productCategories">
        {sortedToolsProductTypes.map((toolsProduct) => {
          const numberOfTools = getNumberOfTools(toolsProduct.productType);

          if (numberOfTools === 0) {
            return <Fragment key={toolsProduct.productType}></Fragment>;
          }

          return (
            <ProductCard
              key={toolsProduct.productType}
              height={productCardHelper.height}
              content={toolsProduct.content}
              localImage={toolsProduct.localImage}
              isSelected={isSelected(toolsProduct.productType)}
              mode={ProductCardMode.MultiSelect}
              onChange={() => {
                onProductTypeChange(toolsProduct.productType);
              }}
              preContent={getPreContent(numberOfTools)}
              title={toolsProduct.name}
              addTooltip={false}
              tooltipText=""
            />
          );
        })}
      </div>
      {productItems && productItems.length > 0 && (
        <ToolsProductItemsList
          onChange={onListObjectChange}
          productItems={productItems}
          refToolsProductItemsList={refToolsProductItemsList}
          selectedToolsProductItems={selectedToolsProductItems}
        />
      )}
    </>
  );
};

export default Tools;
